import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { setAlias, checkAliasAvailability } from '../../services/aliasService';
import { Button } from '../ui/Button';

interface AliasManagerProps {
  currentAlias?: string;
  topicId?: string;
  onAliasChanged?: (newAlias: string) => void;
  showInline?: boolean;
}

// Use a valid UUID for the general/default forum alias
// Backend requires UUIDs for topicId
const GENERAL_TOPIC_ID = '00000000-0000-0000-0000-000000000000';

export function AliasManager({
  currentAlias = '',
  topicId = GENERAL_TOPIC_ID,
  onAliasChanged,
  showInline = false
}: AliasManagerProps) {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [newAlias, setNewAlias] = useState(currentAlias);
  const [isChecking, setIsChecking] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setNewAlias(currentAlias);
  }, [currentAlias]);

  useEffect(() => {
    if (!isEditing) return;

    const trimmedAlias = newAlias.trim();

    // Reset if empty or same as current
    if (!trimmedAlias || trimmedAlias === currentAlias) {
      setIsAvailable(null);
      setError(null);
      return;
    }

    // Validate alias format
    if (trimmedAlias.length < 3) {
      setIsAvailable(false);
      setError('Alias must be at least 3 characters');
      return;
    }

    if (trimmedAlias.length > 30) {
      setIsAvailable(false);
      setError('Alias must be at most 30 characters');
      return;
    }

    if (!/^[a-zA-Z0-9_-]+$/.test(trimmedAlias)) {
      setIsAvailable(false);
      setError('Alias can only contain letters, numbers, underscores, and hyphens');
      return;
    }

    // Debounce uniqueness check
    const timeoutId = setTimeout(async () => {
      setIsChecking(true);
      setError(null);

      try {
        const available = await checkAliasAvailability(trimmedAlias);
        setIsAvailable(available);
        if (!available) {
          setError('This alias is already taken');
        }
      } catch (err) {
        setError('Failed to check alias availability');
        setIsAvailable(false);
      } finally {
        setIsChecking(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [newAlias, currentAlias, isEditing]);

  const handleSave = async () => {
    const trimmedAlias = newAlias.trim();

    if (!trimmedAlias || trimmedAlias === currentAlias) {
      setIsEditing(false);
      return;
    }

    if (!isAvailable) {
      setError('Please choose a valid and available alias');
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await setAlias(topicId, trimmedAlias);
      setSuccess('Alias updated successfully!');
      setIsEditing(false);

      if (onAliasChanged) {
        onAliasChanged(trimmedAlias);
      }

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update alias');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setNewAlias(currentAlias);
    setIsEditing(false);
    setError(null);
    setIsAvailable(null);
  };

  if (!user) {
    return null;
  }

  if (showInline) {
    return (
      <div className="space-y-3">
        {/* Display Mode */}
        {!isEditing && (
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm font-medium text-slate-600 dark:text-slate-400 mb-1">
                Forum Alias
              </div>
              <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {currentAlias || 'Not set'}
              </div>
              {!currentAlias && (
                <p className="text-sm text-slate-500 dark:text-slate-500 mt-1">
                  Set your forum alias to personalize your identity
                </p>
              )}
            </div>
            <Button
              onClick={() => setIsEditing(true)}
              variant="secondary"
              size="sm"
            >
              {currentAlias ? 'Change' : 'Set Alias'}
            </Button>
          </div>
        )}

        {/* Edit Mode */}
        {isEditing && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Forum Alias *
              </label>
              <input
                type="text"
                value={newAlias}
                onChange={(e) => setNewAlias(e.target.value)}
                className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-3 py-2 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                placeholder="Enter your alias"
                maxLength={30}
                disabled={isSaving}
              />

              {/* Validation feedback */}
              {isChecking && (
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                  Checking availability...
                </p>
              )}

              {!isChecking && isAvailable === true && newAlias.trim() && newAlias.trim() !== currentAlias && (
                <p className="text-sm text-green-600 dark:text-green-400 mt-1 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Available
                </p>
              )}

              {error && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-1 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {error}
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleSave}
                disabled={isSaving || isChecking || !isAvailable || newAlias.trim() === currentAlias}
                className="flex-1"
              >
                {isSaving ? 'Saving...' : 'Save Alias'}
              </Button>
              <Button
                onClick={handleCancel}
                variant="secondary"
                disabled={isSaving}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Success message */}
        {success && (
          <div className="bg-green-100 dark:bg-green-900/20 border border-green-300 dark:border-green-800 rounded p-3">
            <p className="text-sm text-green-700 dark:text-green-400">{success}</p>
          </div>
        )}
      </div>
    );
  }

  // Card mode (default)
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Forum Alias</h2>
        {!isEditing && (
          <Button
            onClick={() => setIsEditing(true)}
            variant="secondary"
            size="sm"
          >
            {currentAlias ? 'Change' : 'Set Alias'}
          </Button>
        )}
      </div>

      {/* Display Mode */}
      {!isEditing && (
        <div>
          <div className="text-2xl font-bold text-brand-600 dark:text-brand-400 mb-2">
            {currentAlias || 'Not set'}
          </div>
          {!currentAlias && (
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Set your forum alias to personalize your identity across all topics.
              Your alias must be unique and can be changed at any time.
            </p>
          )}
          {currentAlias && (
            <p className="text-sm text-slate-600 dark:text-slate-400">
              This is your current forum alias. You can change it at any time.
            </p>
          )}
        </div>
      )}

      {/* Edit Mode */}
      {isEditing && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              New Alias *
            </label>
            <input
              type="text"
              value={newAlias}
              onChange={(e) => setNewAlias(e.target.value)}
              className="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-4 py-3 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              placeholder="Enter your alias"
              maxLength={30}
              disabled={isSaving}
              autoFocus
            />

            <div className="mt-2 text-xs text-slate-500 dark:text-slate-500">
              • 3-30 characters
              <br />
              • Letters, numbers, underscores, and hyphens only
              <br />
              • Must be unique
            </div>

            {/* Validation feedback */}
            {isChecking && (
              <div className="mt-2 text-sm text-slate-500 dark:text-slate-400 flex items-center">
                <svg className="animate-spin h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Checking availability...
              </div>
            )}

            {!isChecking && isAvailable === true && newAlias.trim() && newAlias.trim() !== currentAlias && (
              <div className="mt-2 text-sm text-green-600 dark:text-green-400 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                This alias is available!
              </div>
            )}

            {error && (
              <div className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            )}
          </div>

          <div className="flex gap-3">
            <Button
              onClick={handleSave}
              disabled={isSaving || isChecking || !isAvailable || newAlias.trim() === currentAlias}
              className="flex-1"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
            <Button
              onClick={handleCancel}
              variant="secondary"
              disabled={isSaving}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Success message */}
      {success && (
        <div className="mt-4 bg-green-100 dark:bg-green-900/20 border border-green-300 dark:border-green-800 rounded p-4">
          <p className="text-sm text-green-700 dark:text-green-400 flex items-center">
            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            {success}
          </p>
        </div>
      )}
    </div>
  );
}
